import styled from "@emotion/styled";
import { Box, CircularProgress, Pagination } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useContext, useMemo, useState } from "react";
import { AiOutlineSearch as SearchIcon } from "react-icons/ai";
import { IoReload as ReloadIcon } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  FetchAllRoutes,
  reOrderFetchedData,
} from "../../../../hooks/FetchData";
import { NotificationContext } from "../../../../providers/notification";
import { RootState } from "../../../../redux/store";
import { updateParams } from "../../../../reduxSlice/pendingApprovalParamsSlice";
import { RouteResponse } from "../../../../types/index";
import { FilterDropDown } from "../../../../ui-components";
import { useDebounce } from "../../../../hooks/useDebounce";
import { ExNyRoute } from "../../../../utils/allRouteId";
import apiCall from "../../../../utils/apiCall";
import styles from "../../Applicants/css/display-applicant.module.css";
import css from "../css/approve-modal.module.css";
import BatchApprovalModal from "../Modal/BatchApprove";
import { ApprovalContext } from "./Approvals";
import ApprovalTable from "./ApprovalTable";
import BatchApprovalTable from "./BatchApprovalTable";

//Validation
// const validationSchema = Yup.object().shape({
//     comment: Yup.string().required('Comment name is required'),
// });
const validationSchema = Yup.object().shape({
  comment: Yup.string().required(),
});

//Interface
interface RoleProps {
  Id: string;
  Name: string;
  NormalizedName: string;
}

interface RoleResponse {
  data: RoleProps[];
}

interface GetAllPendingApprovals {
  ApplicantId?: any;
  TitleId: number;
  LastName: string;
  FirstName: string;
  MiddleName: string;
  Email: string;
  PhoneNumber: string;
  CorprateName?: any;
  StateCode?: any;
  StartDate?: any;
  isCorporate?: any;
  DateBirth?: any;
  ContactAddress?: any;
  StateOriginId?: any;
  LGAId?: any;
  StateId?: any;
  ZoneId?: any;
  NYSCUploadDocumentUrl?: any;
  GenderId?: any;
  MaritalStatusId?: any;
  IsChangeofName?: any;
  UploadPictureUrl?: any;
  UploadChangeofNameUrl?: any;
  TabId?: any;
  IsSubmitted: boolean;
  RouteId: number;
  AppMemberId: string;
  TargetedMembers?: any;
  TargetedMembersId: number;
  ApprovalRoleId: string;
  ApprovalIndex: number;
  Status: number;
  FinalApproval: number;
  RouteName: string;
  AppprovalComment?: any;
  ApprovalActionTypes: number;
  AppprovalAction?: any;
  ApprovalActionTypeId?: any;
  ApplicationDate: string;
}

interface PagedPendingApprovals {
  TotalSize: number;
  PageNumber: number;
  PageSize: number;
  TotalCount: number;
  PageCount: number;
  StatusCode: number;
  Message: string;
  Data: GetAllPendingApprovals[];
}

interface GetAllPendingApprovalsResponse {
  data: PagedPendingApprovals;
}

interface ExamEntryLevelsData {
  Id: number;
  Name: string;
  RouteId: number;
  ExamAmount: number;
  ExemptionAmount: number;
  IsActive: boolean;
}

export const DisplayApproval = (props: any) => {
  const roleId = useSelector((state: RootState) => state?.user.roleId);
  const userId = useSelector((state: RootState) => state?.user?.userId);
  const batchApprovalData = useSelector(
    (state: RootState) => state?.batchApproval.data
  );
  const pendingApprovalParams = useSelector(
    (state: RootState) => state?.updatePendingApprovalParams
  );
  const [batchApprovalComment, setBatchApprovalComment] = useState("");
  const [route, setRoute] = useState<any>("");
  const [id, setId] = useState(roleId); //pendingApprovalParams?.roleId
  const [routeId, setRouteId] = useState<number>(); //pendingApprovalParams?.roleId
  const [approvalTypeId, setApprovalTypeId] = useState(""); //pendingApprovalParams?.approvalTypeId;
  const [isApproving, setIsApproving] = useState(false);
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData.trim(), 500);
  const [prevDebouncedSearch, setPrevDebouncedSearch] = useState(debouncedSearch);
  const [roleName, setRoleName] = useState("");
  const [EntryLevelId, setEntryLevelId] = useState<number>();
  const [studentTypeId, setStudentTypeId] = useState<number>();
  const [studentTypeName, setStudentTypeName] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);

  // A new search term starts again from page 1. Adjusting state during render
  // (rather than in an effect) avoids an extra request for the old page.
  if (debouncedSearch !== prevDebouncedSearch) {
    setPrevDebouncedSearch(debouncedSearch);
    setPageNumber(1);
  }

  const queryClient = useQueryClient();
  const { showNotification } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const { setRouteName } = useContext(ApprovalContext);

  //const setRouteName = props?.setRouteName;
  const showBatchApprovalModal = props?.showBatchApprovalModal;
  const handleCloseBatchApproval = props?.handleCloseBatchApproval;
  // const entryLevelData = props?.entryLevelData;

  // const routeId = route ? route?.val : '';
  // const approvalTypeId = statusF?.val || '';

  const { isLoading, data } = FetchAllRoutes();

  const allRoutes = useMemo(
    () =>
      isLoading || !data
        ? []
        : reOrderFetchedData((data as RouteResponse)?.Data).map((item) => {
            return { name: item?.RouteName, val: item?.RouteId };
          }),
    [isLoading, data]
  );

  const getAllPendingApprovals =
    async (): Promise<GetAllPendingApprovalsResponse> => {
      const url = `/Approval/GetAllPendingApprovalsByQueryString?expectedValues=%3FRoleId%3D${id}%26RouteId%3D${routeId}%26ApprovalTypeId%3D${approvalTypeId}%26membershipTypeId%3D%26studentType%3D${studentTypeId}&PageNumber=${pageNumber}&PageSize=${PAGE_SIZE}&search=${encodeURIComponent(debouncedSearch)}`;
      return await apiCall().get(url);
    };

  const postBatchApproval = async (data: any[]): Promise<any> => {
    return await apiCall().post("/Approval/NextApprovalBulk", data);
  };

  const getAllExamEntryLevels = async (): Promise<ExamEntryLevelsData[]> => {
    return (await apiCall().get("/SetUp/GetAllExemption"))?.data
      ?.Data as ExamEntryLevelsData[];
  };

  const batchApprovalMutation = useMutation(
    ["post-batch-approval"],
    postBatchApproval,
    { retry: 0 }
  );

  const handlePostBatchApproval = async (action: string) => {
    const response = await validationSchema
      .isValid({
        comment: batchApprovalComment,
      })
      .then((valid) => valid);
    // const body = new FormData();

    const batchApprovalPayloadList = batchApprovalData.map((item) => {
      return {
        ApprovalTypeId: 1,
        RouteId: item?.RouteId,
        AppMemberId: item?.AppMemberId,
        TargetedMembers: "Applicants",
        TargetedMembersId: item?.TargetedMembersId,
        ApprovalRoleId: item?.ApprovalRoleId,
        ApprovalIndex: item?.ApprovalIndex,
        Status: item?.Status,
        FinalApproval: item?.FinalApproval,
        ApprovalComment: "",
        ApprovalActionTypeId: "1",
        ApprovalActionTypes: action,
        ApprovalAction: action,
        Email: item?.Email,
        ApprovalUserId: userId,
        ExemptionId: EntryLevelId ? String(EntryLevelId) : 1,
        examId: 1,
      };
    });

    action === "Approved" ? setIsApproving(true) : setIsApproving(false);

    try {
      // if(!response) throw 'Comment is Required';
      if (!Response) {
        // Do nothing
      }
      if (routeId == ExNyRoute && !EntryLevelId)
        throw new Error("Set Exam Entry Level");
      if (batchApprovalPayloadList.length < 1)
        throw new Error("select applicants to approve");
      await batchApprovalMutation.mutateAsync(batchApprovalPayloadList, {
        onSuccess: () => {
          showNotification({
            message: "Batch Approval Successful",
            type: "success",
          });
          queryClient.invalidateQueries(["get-approval"]);
          handleCloseBatchApproval();
          setBatchApprovalComment("");
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error.message || error,
        type: "error",
      });
    }
  };

  const postBatchDecline = async (data: any[]): Promise<any> => {
    return await apiCall().post("/Approval/ApprovalDeclinedBulk", data);
  };

  const batchDeclineMutation = useMutation(
    ["post-batch-decline"],
    postBatchDecline,
    { retry: 0 }
  );

  const handlePostBatchDecline = async (action: string) => {
    const response = await validationSchema
      .isValid({
        comment: batchApprovalComment,
      })
      .then((valid) => valid);

    const batchDeclinePayloadList = batchApprovalData.map((item) => {
      return {
        ApprovalTypeId: 1,
        RouteId: item?.RouteId,
        AppMemberId: item?.AppMemberId,
        TargetedMembers: "Applicants",
        TargetedMembersId: item?.TargetedMembersId,
        ApprovalRoleId: item?.ApprovalRoleId,
        ApprovalIndex: item?.ApprovalIndex,
        Status: item?.Status,
        FinalApproval: item?.FinalApproval,
        ApprovalComment: batchApprovalComment,
        ApprovalActionTypeId: "2",
        ApprovalActionTypes: action,
        ApprovalAction: action,
        Email: item?.Email,
        ApprovalUserId: userId,
        ExemptionId: 1,
        examId: 1,
      };
    });

    action === "Approved" ? setIsApproving(true) : setIsApproving(false);

    try {
      if (!response) throw new Error("Comment is Required");

      await batchDeclineMutation.mutateAsync(batchDeclinePayloadList, {
        onSuccess: () => {
          showNotification({
            message: "Approval Decline Successful",
            type: "success",
          });
          queryClient.invalidateQueries(["get-approval"]);
          handleCloseBatchApproval();
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error.message || error,
        type: "error",
      });
    }
  };

  // Keyed on the debounced term, not searchData, so typing doesn't fire a
  // request per keystroke.
  const getAllPendingApprovalQuery = useQuery(
    [
      "get-approval",
      id,
      routeId,
      approvalTypeId,
      studentTypeId,
      pageNumber,
      debouncedSearch,
    ],
    getAllPendingApprovals,
    { refetchOnWindowFocus: false, keepPreviousData: true }
  );

  const getAllEntryLevelQuery = useQuery(
    ["get-entry-level"],
    getAllExamEntryLevels,
    { enabled: routeId == ExNyRoute, refetchOnWindowFocus: false }
  );

  const entryLevelData = getAllEntryLevelQuery?.data;

  const getRoleByUserId = async () => {
    return (await apiCall().post(
      `Authentication/GetRoleByUserId?userid=${userId}`
    )) as RoleResponse;
  };

  const getRoleByUserIdQuery = useQuery(
    ["get-roleById", userId],
    getRoleByUserId,
    { retry: 0, refetchOnWindowFocus: false, enabled: !!userId }
  );

  const roleError = getRoleByUserIdQuery?.error as AxiosError;
  const roleErrorMessage = roleError?.message;

  const mappedRoleData: any[] = useMemo(
    () =>
      (getRoleByUserIdQuery?.data?.data ?? []).map((item: RoleProps) => ({
        val: item?.Id,
        name: item?.Name,
      })),
    [getRoleByUserIdQuery?.data]
  );

  const handleRoleIdChange = (role: { val: string; name: string }) => {
    setId(role?.val);
    setRoleName(role?.name);
    setPageNumber(1);
    dispatch(
      updateParams({
        ...pendingApprovalParams,
        roleId: role.val,
      })
    );
  };

  const handleRouteIdChange = (route: { val: number; name: string }) => {
    setRouteId(route?.val);
    setPageNumber(1);

    setRouteName(route?.name);
    setRoute(route?.name);

    dispatch(
      updateParams({
        ...pendingApprovalParams,
        routeId: route.val?.toString(),
      })
    );
  };

  const handleStudentTypeChange = (type: { val: number; name: string }) => {
    setStudentTypeId(type.val);
    setStudentTypeName(type.name);
    setPageNumber(1);
  };

  // const mappedRouteData: any[] | undefined = routeData?.map((item) => {
  //   let route = {
  //     val: item?.RouteId,
  //     name: item?.RouteName,
  //   };

  //   return route;
  // });

  const handleApproveBatchApproval = async () => {
    await handlePostBatchApproval("Approved");
  };

  const handleRejectBatchApproval = async () => {
    await handlePostBatchDecline("Rejected");
  };

  const pagedResponse = getAllPendingApprovalQuery?.data?.data;

  const pendingApprovals = useMemo(
    () => pagedResponse?.Data ?? [],
    [pagedResponse]
  );

  // The API reports the total record count in TotalSize; TotalCount and
  // PageCount currently come back as 0, so they are only used if populated.
  const totalRecords = pagedResponse?.TotalSize || pagedResponse?.TotalCount || 0;
  const pageCount =
    pagedResponse?.PageCount ||
    Math.ceil(totalRecords / (pagedResponse?.PageSize || PAGE_SIZE));

  const SwitchEntryLevel = (data: { val: number; name: string }) => {
    setEntryLevelId(data?.val);
  };

  const entryLevelOptions = useMemo(
    () =>
      entryLevelData
        ? entryLevelData?.map((item: ExamEntryLevelsData) => {
            return {
              val: item?.Id,
              name: item?.Name,
            };
          })
        : [],
    [entryLevelData]
  );

  const pagination =
    pageCount > 1 ? (
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Pagination
          count={pageCount}
          page={pageNumber}
          onChange={(_, page) => setPageNumber(page)}
          disabled={getAllPendingApprovalQuery?.isFetching}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </Box>
    ) : null;

  // Only the very first load (nothing to show yet) replaces the table with a
  // spinner. Later fetches keep the previous rows in place under an overlay,
  // so the table height and the pager position stay put.
  const isInitialLoading = getAllPendingApprovalQuery?.isLoading;
  const isRefetching = getAllPendingApprovalQuery?.isFetching;

  const initialSpinner = (
    <CircularProgress
      sx={{
        fontSize: "1rem",
        marginInline: "auto",
        display: "block",
        marginBlock: "2rem",
      }}
    />
  );

  return (
    <div className={styles.container}>
      <div className={styles.filterDiv}>
        <label className={styles.searchForm}>
          <span className={styles.sI}>
            <SearchIcon />
          </span>
          <input
            value={searchData}
            onChange={(e) => setSearchData(e.target.value)}
            type="text"
            placeholder="Search"
          />
          <button className={styles.rBtn} type="submit">
            <ReloadIcon />
          </button>
        </label>

        <FilterDropDown
          onChange={handleRouteIdChange}
          text={route ? `${route}` : "Membership Route"}
          bg="#0A3922"
          color="#fff"
          options={allRoutes}
        />

        {routeId === 1 && (
          <FilterDropDown
            onChange={handleStudentTypeChange}
            text={studentTypeName ? studentTypeName : "Student Type"}
            bg="#E2E4E9"
            options={studentType}
          />
        )}

        {/* <FilterDropDown
          onChange={setStatusF}
          text="Approval Type"
          bg="#E2E4E9"
          options={mappedApprovalTypeData}
        /> */}
        {/* <FilterDropDown onChange={setStatusF} text="Actions" bg="#E2E4E9" options={option4} /> */}
        {/* <FilterDropDown onChange={setStatusF} text="Status" bg="#E2E4E9" options={optb} /> */}
        <FilterDropDown
          onChange={handleRoleIdChange}
          text={roleName ? roleName : "Role"}
          options={mappedRoleData}
        />
      </div>

      <div className={styles.div}>
        {isInitialLoading ? (
          initialSpinner
        ) : getRoleByUserIdQuery.isError ? (
          <StyledError>{roleErrorMessage}</StyledError>
        ) : (
          <LoadingOverlay loading={isRefetching}>
            {pendingApprovals.length > 0 ? (
              <ApprovalTable key={pageNumber} data={pendingApprovals} />
            ) : (
              <NoResult>No Results</NoResult>
            )}
          </LoadingOverlay>
        )}
        {pagination}

        <BatchApprovalModal
          open={showBatchApprovalModal}
          cancel={handleCloseBatchApproval}
          header={"Batch Approval"}
          maxWidth={"1300px"}
          content={
            <Container>
              {isInitialLoading ? (
                initialSpinner
              ) : getRoleByUserIdQuery.isError ? (
                <StyledError>{roleErrorMessage}</StyledError>
              ) : (
                <LoadingOverlay loading={isRefetching}>
                  <BatchApprovalTable
                    setSearchData={setSearchData}
                    searchData={searchData}
                    data={pendingApprovals}
                  />
                </LoadingOverlay>
              )}
              {pagination}
              <div style={{ paddingBlock: "20px" }}>
                {routeId == ExNyRoute && (
                  <FilterDropDown
                    onChange={SwitchEntryLevel}
                    width100={true}
                    text={"Exam Entry Levels"}
                    bg="#0A3922"
                    color="#fff"
                    options={entryLevelOptions}
                    // replace={"true"}
                  />
                )}
              </div>

              <div className={css.comment}>
                <div className={css.title}>Comment</div>
                <textarea
                  value={batchApprovalComment}
                  onChange={(e) => setBatchApprovalComment(e.target.value)}
                  rows={6}
                ></textarea>
              </div>
              <div className={css.btnDiv}>
                <button
                  disabled={
                    batchApprovalMutation.isLoading ||
                    batchDeclineMutation.isLoading
                  }
                  style={{
                    border: "1px solid #EB5757",
                    color: "#EB5757",
                    cursor: "pointer",
                  }}
                  onClick={handleRejectBatchApproval}
                >
                  {batchDeclineMutation.isLoading ? "Declining..." : "Decline"}
                </button>
                <button
                  disabled={
                    batchApprovalMutation.isLoading ||
                    batchDeclineMutation.isLoading
                  }
                  onClick={handleApproveBatchApproval}
                  style={{
                    border: "1px solid #5BC065",
                    color: "#fff",
                    backgroundColor: "#5BC065",
                    cursor: "pointer",
                  }}
                >
                  {batchApprovalMutation.isLoading ? "Approving..." : "Approve"}
                </button>
              </div>
            </Container>
          }
        />
      </div>
    </div>
  );
};

const PAGE_SIZE = 30;

const studentType = [
  { name: "Exam", val: 1 },
  { name: "NYSC", val: 2 },
];

const LoadingOverlay = ({
  loading,
  children,
}: {
  loading?: boolean;
  children: React.ReactNode;
}) => (
  <OverlayWrapper aria-busy={loading}>
    {children}
    {loading && (
      <div className="overlay">
        <CircularProgress />
      </div>
    )}
  </OverlayWrapper>
);

const OverlayWrapper = styled("div")`
  position: relative;

  & > .overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 4rem;
    background-color: rgba(255, 255, 255, 0.6);
    z-index: 1;
  }
`;

const StyledError = styled("p")`
  color: red;
  font-size: 1.4rem;
  display: flex;
`;

const Container = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: 48px;
`;
const NoResult = styled.div`
  margin-block-start: 0px;
`;
